/**
 * `accounting.*` — the thin general ledger (ADR-0027).
 *
 * Read models (Trial Balance / P&L / Balance Sheet) + the manual journal
 * voucher + the Phase-0 rebuild. Money crosses the wire as paise strings:
 * `moneyPaise` (non-negative) for JV inputs, `signedPaise` for statement
 * balances (which can be negative — credit-card, drawings, a loss).
 */
import { z } from "zod";
import { isoDate, moneyPaise, signedPaise } from "../_common.js";

const accountType = z.enum(["asset", "liability", "equity", "income", "expense"]);

// ── Chart (for the JV account picker and chart management) ──
export const chartAccountsInput = z.object({});
export const chartAccountsOutput = z.object({
  accounts: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      type: accountType,
      isSystem: z.boolean(),
      icon: z.string().nullable(),
    }),
  ),
});

export const createAccountInput = z.object({
  /** Lowercase slug: letters, digits, underscores only. Max 64 chars. */
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, "Code must be lowercase letters, digits, or underscores"),
  name: z.string().trim().min(1).max(120),
  type: accountType,
  /** Lucide-react icon name (e.g. "Landmark"). Optional. */
  icon: z.string().max(64).nullable().optional(),
});
export const createAccountOutput = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  type: accountType,
  normalBalance: z.enum(["debit", "credit"]),
  icon: z.string().nullable(),
});

export const deleteAccountInput = z.object({ code: z.string().min(1) });
export const deleteAccountOutput = z.object({ deleted: z.literal(true) });

// ── Trial Balance ──
export const trialBalanceInput = z.object({});
const trialBalanceRow = z.object({
  code: z.string(),
  name: z.string(),
  type: z.string(),
  debitPaise: moneyPaise,
  creditPaise: moneyPaise,
  balancePaise: signedPaise,
});
export const trialBalanceOutput = z.object({
  rows: z.array(trialBalanceRow),
  totalDebitPaise: moneyPaise,
  totalCreditPaise: moneyPaise,
  isBalanced: z.boolean(),
});

// ── Profit & Loss (defaults to the current Indian FY when dates omitted) ──
export const profitAndLossInput = z.object({
  fromDate: isoDate.optional(),
  toDate: isoDate.optional(),
});
const statementLine = z.object({ code: z.string(), name: z.string(), amountPaise: signedPaise });
export const profitAndLossOutput = z.object({
  fromDate: isoDate,
  toDate: isoDate,
  income: z.array(statementLine),
  totalIncomePaise: signedPaise,
  expenses: z.array(statementLine),
  totalExpensePaise: signedPaise,
  netProfitPaise: signedPaise,
});

// ── Balance Sheet (defaults to as-at today) ──
export const balanceSheetInput = z.object({ asOf: isoDate.optional() });
export const balanceSheetOutput = z.object({
  asOf: isoDate,
  assets: z.array(statementLine),
  totalAssetsPaise: signedPaise,
  liabilities: z.array(statementLine),
  totalLiabilitiesPaise: signedPaise,
  equity: z.array(statementLine),
  totalEquityPaise: signedPaise,
  currentEarningsPaise: signedPaise,
  isBalanced: z.boolean(),
});

// ── Per-document journal entry (the "Accounting" card on a bill/payment) ──
/** The document types that post to the ledger and can be drilled into. */
export const ledgerSourceType = z.enum([
  "invoice",
  "credit_note",
  "receipt",
  "vendor_bill",
  "vendor_payment",
  "cash_movement",
  "journal",
  "opening_balance",
  "payroll_run",
]);
export const entryForDocumentInput = z.object({
  sourceType: ledgerSourceType,
  sourceId: z.string().uuid(),
});
const entryLine = z.object({
  accountCode: z.string(),
  accountName: z.string(),
  accountType,
  debitPaise: moneyPaise,
  creditPaise: moneyPaise,
  partyType: z.enum(["customer", "vendor"]).nullable(),
  partyId: z.string().uuid().nullable(),
});
export const entryForDocumentOutput = z.object({
  /** `null` when the document has not posted to the ledger (e.g. a draft bill). */
  entry: z
    .object({
      entryId: z.string().uuid(),
      entryDate: isoDate,
      narration: z.string(),
      reversedByEntryId: z.string().uuid().nullable(),
      isBalanced: z.boolean(),
      lines: z.array(entryLine),
      totalDebitPaise: moneyPaise,
      totalCreditPaise: moneyPaise,
    })
    .nullable(),
});

// ── Manual journal voucher ──
export const postJournalInput = z.object({
  entryDate: isoDate,
  narration: z.string().trim().min(1).max(500),
  kind: z.enum(["journal", "opening_balance"]).optional(),
  lines: z
    .array(
      z.object({
        accountCode: z.string().min(1),
        debitPaise: moneyPaise,
        creditPaise: moneyPaise,
      }),
    )
    .min(2)
    .max(100),
});
export const postJournalOutput = z.object({ entryId: z.string().uuid() });

// ── Rebuild from documents (Phase-0 backfill; idempotent) ──
export const rebuildInput = z.object({});
export const rebuildOutput = z.object({
  posted: z.number(),
  skipped: z.number(),
  failed: z.number(),
});

// ── Period lock (ADR-0027 §7) ──
const periodMonth = z.number().int().min(1).max(12);
const periodYear = z.number().int().min(2000).max(2100);
const periodView = z.object({
  year: periodYear,
  month: periodMonth,
  label: z.string(),
  status: z.enum(["open", "closed"]),
  closedAt: z.string().nullable(),
});

/** `fyStartYear` is the Indian FY start year: 2025 → Apr 2025 … Mar 2026. */
export const periodsInput = z.object({ fyStartYear: periodYear });
export const periodsOutput = z.object({
  fyStartYear: periodYear,
  periods: z.array(periodView),
});

export const closePeriodInput = z.object({ year: periodYear, month: periodMonth });
export const closePeriodOutput = periodView;
export const reopenPeriodInput = closePeriodInput;
export const reopenPeriodOutput = periodView;

// ── Opening balance (ADR-0027 §5, go-live) ──
export const openingBalanceStatusInput = z.object({});
export const openingBalanceStatusOutput = z.object({
  posted: z.boolean(),
  asOfDate: isoDate.nullable(),
  entryId: z.string().uuid().nullable(),
});

export const postOpeningBalanceInput = z.object({
  asOfDate: isoDate,
  lines: z
    .array(
      z.object({
        accountCode: z.string().min(1),
        debitPaise: moneyPaise,
        creditPaise: moneyPaise,
      }),
    )
    .min(1)
    .max(100),
});
export const postOpeningBalanceOutput = z.object({
  entryId: z.string().uuid(),
  /** Signed amount auto-plugged to Opening Balance Equity. */
  plugPaise: signedPaise,
});
